import { useEffect, useRef } from 'react';
import { Modal } from 'antd';

import type { Dir, Screen, Enemy, Fireball, Boom, Vec } from './types';
import {
  W, H, DISPLAY_W, DISPLAY_H,
  PW, PH, EW, EH, FB,
  PLAYER_SPEED, ENEMY_BASE_SPEED, ENEMY_SPEED_PER_LVL,
  FB_SPEED, FIRE_CD, BASE_ENEMIES, MAX_ENEMIES,
  LEVEL_PAUSE, INVINCIBLE_TIME, BOOM_DURATION,
  PLAY_TOP, PLAY_BOT,
} from './constants';
import {
  renderIntro, renderPlayfield, renderFireballs, renderExplosions,
  drawWizard, drawBusinessman,
  renderGameOverOverlay, renderLevelUpOverlay,
} from './rendering';

/* ═══════════════════════════════════════════════════════════════════════════
   LEO'S QUEST — A retro NES-style mini-game
   ═══════════════════════════════════════════════════════════════════════════ */

/** AABB overlap test */
function hits(
  ax: number, ay: number, aw: number, ah: number,
  bx: number, by: number, bw: number, bh: number,
) {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

// ── The canvas game component ──

const LeoGame = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;

    // ── Mutable state ─────────────────────────────────────────────
    let screen: Screen = 'intro';
    let level = 1;
    let score = 0;
    let totalTime = 0;
    let blink = 0;
    let levelTimer = 0;
    let invTimer = 0;
    let isMoving = false;

    let px = W / 2 - PW / 2;
    let py = (PLAY_TOP + PLAY_BOT) / 2 - PH / 2;
    let pdir: Dir = 'right';
    let fireCd = 0;

    let enemies: Enemy[] = [];
    let fireballs: Fireball[] = [];
    let booms: Boom[] = [];
    let bushes: Vec[] = [];

    const stars: Vec[] = Array.from({ length: 25 }, () => ({
      x: 22 + Math.random() * (W - 44),
      y: 14 + Math.random() * (H - 28),
    }));

    // ── Input ─────────────────────────────────────────────────────
    const keys = new Set<string>();
    const gameKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'Enter'];

    function onKeyDown(e: KeyboardEvent) {
      if (gameKeys.includes(e.key)) e.preventDefault();
      keys.add(e.key);
    }
    function onKeyUp(e: KeyboardEvent) { keys.delete(e.key); }

    // ── Level setup ───────────────────────────────────────────────
    function startLevel(lvl: number) {
      level = lvl;
      px = W / 2 - PW / 2;
      py = (PLAY_TOP + PLAY_BOT) / 2 - PH / 2;
      pdir = 'right';
      fireCd = 0;
      fireballs = [];
      booms = [];
      invTimer = INVINCIBLE_TIME;
      spawnEnemies(lvl);
      spawnBushes(lvl);
    }

    function spawnEnemies(lvl: number) {
      const count = Math.min(BASE_ENEMIES * Math.pow(2, lvl - 1), MAX_ENEMIES);
      enemies = [];
      for (let i = 0; i < count; i++) {
        const side = Math.floor(Math.random() * 4);
        let ex: number, ey: number;
        if (side === 0)      { ex = Math.random() * W; ey = PLAY_TOP - EH - 4; }
        else if (side === 1) { ex = Math.random() * W; ey = PLAY_BOT + 4; }
        else if (side === 2) { ex = -EW - 4; ey = PLAY_TOP + Math.random() * (PLAY_BOT - PLAY_TOP); }
        else                 { ex = W + 4;   ey = PLAY_TOP + Math.random() * (PLAY_BOT - PLAY_TOP); }
        enemies.push({
          x: ex, y: ey, alive: true,
          speed: ENEMY_BASE_SPEED + Math.random() * 8 + lvl * ENEMY_SPEED_PER_LVL,
        });
      }
    }

    function spawnBushes(lvl: number) {
      bushes = [];
      for (let i = 0; i < 5 + Math.min(lvl, 10); i++) {
        bushes.push({
          x: 8 + Math.random() * (W - 16),
          y: PLAY_TOP + 8 + Math.random() * (PLAY_BOT - PLAY_TOP - 24),
        });
      }
    }

    // ── Update ────────────────────────────────────────────────────
    function update(dt: number) {
      totalTime += dt;
      blink += dt;

      if (screen === 'intro' || screen === 'gameover') {
        if (keys.has('Enter')) {
          screen = 'playing'; score = 0; startLevel(1); keys.delete('Enter');
        }
        return;
      }
      if (screen === 'levelup') {
        levelTimer -= dt;
        if (levelTimer <= 0) { screen = 'playing'; startLevel(level + 1); }
        return;
      }

      updatePlayer(dt);
      updateFireballs(dt);
      updateEnemies(dt);
      checkCollisions();

      for (const b of booms) b.t -= dt;
      booms = booms.filter(b => b.t > 0);
    }

    function updatePlayer(dt: number) {
      invTimer = Math.max(0, invTimer - dt);

      let dx = 0, dy = 0;
      if (keys.has('ArrowUp'))    { dy = -1; pdir = 'up'; }
      if (keys.has('ArrowDown'))  { dy = 1;  pdir = 'down'; }
      if (keys.has('ArrowLeft'))  { dx = -1; pdir = 'left'; }
      if (keys.has('ArrowRight')) { dx = 1;  pdir = 'right'; }
      if (dx && dy) { const n = 1 / Math.SQRT2; dx *= n; dy *= n; }
      isMoving = dx !== 0 || dy !== 0;

      px = Math.max(0, Math.min(W - PW, px + dx * PLAYER_SPEED * dt));
      py = Math.max(PLAY_TOP, Math.min(PLAY_BOT - PH, py + dy * PLAYER_SPEED * dt));

      fireCd = Math.max(0, fireCd - dt);
      if (keys.has(' ') && fireCd <= 0) {
        fireCd = FIRE_CD;
        const vecs: Record<Dir, [number, number]> = {
          up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0],
        };
        const [fdx, fdy] = vecs[pdir];
        fireballs.push({
          x: px + PW / 2 - FB / 2, y: py + PH / 2 - FB / 2,
          dx: fdx * FB_SPEED, dy: fdy * FB_SPEED,
        });
      }
    }

    function updateFireballs(dt: number) {
      for (const f of fireballs) { f.x += f.dx * dt; f.y += f.dy * dt; }
      fireballs = fireballs.filter(
        f => f.x > -20 && f.x < W + 20 && f.y > -20 && f.y < H + 20,
      );
    }

    function updateEnemies(dt: number) {
      for (const en of enemies) {
        if (!en.alive) continue;
        const edx = (px + PW / 2) - (en.x + EW / 2);
        const edy = (py + PH / 2) - (en.y + EH / 2);
        const dist = Math.sqrt(edx * edx + edy * edy);
        if (dist > 1) {
          en.x += (edx / dist) * en.speed * dt;
          en.y += (edy / dist) * en.speed * dt;
        }
      }
    }

    function checkCollisions() {
      // Fireball → enemy
      for (const f of fireballs) {
        for (const en of enemies) {
          if (!en.alive) continue;
          if (hits(f.x, f.y, FB, FB, en.x, en.y, EW, EH)) {
            en.alive = false;
            f.x = -999;
            score += 100;
            booms.push({ x: en.x + EW / 2, y: en.y + EH / 2, t: BOOM_DURATION });
          }
        }
      }
      fireballs = fireballs.filter(f => f.x > -50);

      // Enemy → player
      if (invTimer <= 0) {
        for (const en of enemies) {
          if (!en.alive) continue;
          if (hits(px + 2, py + 2, PW - 4, PH - 4, en.x, en.y, EW, EH)) {
            screen = 'gameover';
            return;
          }
        }
      }

      // Level complete?
      if (enemies.every(e => !e.alive)) {
        screen = 'levelup';
        levelTimer = LEVEL_PAUSE;
      }
    }

    // ── Render ────────────────────────────────────────────────────
    function render() {
      ctx.clearRect(0, 0, W, H);

      if (screen === 'intro') {
        renderIntro(ctx, totalTime, blink, stars);
        return;
      }

      // All non-intro screens share the playfield
      const frame = Math.floor(totalTime * 4) % 2;
      const aliveCount = enemies.filter(e => e.alive).length;
      renderPlayfield(ctx, bushes, level, score, aliveCount);
      renderFireballs(ctx, fireballs);

      for (const en of enemies) {
        if (en.alive) drawBusinessman(ctx, en.x, en.y, frame);
      }

      if (invTimer <= 0 || Math.sin(totalTime * 20) > 0) {
        drawWizard(ctx, px, py, pdir, isMoving ? frame : 0, totalTime);
      }

      renderExplosions(ctx, booms);

      if (screen === 'gameover') renderGameOverOverlay(ctx, score, level, blink);
      if (screen === 'levelup') renderLevelUpOverlay(ctx, level);
    }

    // ── Main loop ─────────────────────────────────────────────────
    let prevTime = 0;
    let frameId = 0;

    function loop(t: number) {
      const dt = Math.min((t - prevTime) / 1000, 0.05);
      prevTime = t;
      update(dt);
      render();
      frameId = requestAnimationFrame(loop);
    }

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    frameId = requestAnimationFrame((t) => { prevTime = t; loop(t); });

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={W}
      height={H}
      style={{
        width: DISPLAY_W,
        height: DISPLAY_H,
        imageRendering: 'pixelated',
        display: 'block',
        background: '#000',
        outline: 'none',
      }}
    />
  );
};

// ── Modal wrapper ──

interface LeoGameModalProps {
  open: boolean;
  onClose: () => void;
}

const LeoGameModal = ({ open, onClose }: LeoGameModalProps) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    width={DISPLAY_W + 48}
    centered
    destroyOnClose
    title={null}
    closable
    styles={{
      body: { padding: 12, display: 'flex', justifyContent: 'center' },
      container: { background: '#0a0a1a', padding: 0 },
    }}
  >
    <LeoGame />
  </Modal>
);

export { LeoGame };
export default LeoGameModal;
